import { yupResolver } from '@hookform/resolvers/yup';
import { LinkOutlined } from '@mui/icons-material';
import { Box, Stack, styled, Typography } from '@mui/material';
import { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ButtonWithLoader, ConnectedTextField } from 'shared/components';
import * as yup from 'yup';

import { useSendPortalLinkMutation } from '../../hooks';

interface SendLinkFormData {
    email: string;
}

const schema = yup.object({
    email: yup.string().required().email()
});

export default function SendLinkForm() {
    const { t } = useTranslation('auth', { keyPrefix: 'loginHelp.sendLink' });
    const { t: tHelp } = useTranslation('auth', { keyPrefix: 'loginHelp' });

    const methods = useForm<SendLinkFormData>({
        defaultValues: { email: '' },
        resolver: yupResolver(schema),
        mode: 'onChange'
    });

    const { handleSubmit, reset, control } = methods;
    const { mutate: sendLink, isPending } = useSendPortalLinkMutation();

    const onSubmit = useCallback((data: SendLinkFormData) => {
        sendLink(data.email.trim(), {
            onSuccess: () => reset()
        });
    }, [ sendLink, reset ]);

    return (
        <>
            <AuthDivider>{ tHelp('divider') }</AuthDivider>

            <Card>
                <Header>
                    <LinkOutlined fontSize="small" />
                    <Typography variant="subtitle2"
                        gutterBottom
                    >
                        { t('title') }
                    </Typography>
                </Header>

                <Typography variant="body2"
                    color="text.secondary"
                >
                    { t('description') }
                </Typography>

                <FormProvider { ...methods }>
                    <Form onSubmit={ handleSubmit(onSubmit) }>
                        <ConnectedTextField
                            name="email"
                            control={ control }
                            placeholder={ t('emailPlaceholder') }
                            size="small"
                            fullWidth
                        />
                        <ButtonWithLoader
                            type="submit"
                            variant="outlined"
                            color="inherit"
                            loading={ isPending }
                            size="small"
                        >
                            { t('button') }
                        </ButtonWithLoader>
                    </Form>
                </FormProvider>
            </Card>
        </>
    );
}

const Card = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.spacing(2),
    padding: theme.spacing(6),
    width: '100%'
}));

const Header = styled(Stack)({
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8
});

const AuthDivider = styled(Box)(({ theme }) => ({
    '::before, ::after': {
        content: '""',
        flex: 1,
        height: 1,
        backgroundColor: theme.palette.divider
    },

    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),
    color: theme.palette.text.disabled
}));

const Form = styled('form')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(4),
    input: {
        backgroundColor: theme.palette.background.paper
    },

    button: {
        paddingTop: 0,
        paddingBottom: 0
    }
}));
